/* eslint-disable @angular-eslint/no-input-rename */
/* eslint-disable @typescript-eslint/naming-convention */
import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { CalcValidators } from '@app/core/directives/validators/calc-validators';
import { MetabolicoService } from '@app/core/services/api';
import { debounceTime } from 'rxjs/operators';

interface GlycemicComplicationNode {
  key?: string;
  childrens?: GlycemicComplicationNode[];
}

@Component({
  selector: 'app-metabolic-calc-glycemic-profile',
  templateUrl: './metabolic-calc-glycemic-profile.component.html',
  styleUrls: ['./metabolic-calc-glycemic-profile.component.scss']
})
export class MetabolicCalcGlycemicProfileComponent implements OnInit {
  @Input() data: { [key: string]: any } = {};

  _age = 0;
  _unitMeasurement = '';

  glycemicComplications: GlycemicComplicationNode[] = [];
  glycemicComplicationsKeys: string[] = [];
  neurologicalComplicationsKeys: string[] = [];
  retinopathyComplicationsKeys: string[] = [];

  glycemicProfile: FormGroup = new FormGroup({
    diabetes: new FormControl(false),
    glucose: new FormControl('', []),
    HBA1C: new FormControl('', []),
    hasTreatment: new FormControl('no'),
    treatment: new FormControl([]),
    yearsSinceDiabetesDiagnosis: new FormControl({ value: '', disabled: true }),
    complicationsExists: new FormControl('', []),
    complications: new FormControl([]),
    neurologicalType: new FormControl(''),
    retinopatiaType: new FormControl('')
  });

  constructor(private formGroupDirective: FormGroupDirective, private metabolicoService: MetabolicoService) {}

  @Input('age') set setAge(value: number) {
    this._age = value;
    this.addYearsSinceDiabetesDiagnosisValidator();
  }

  @Input('unitMeasurement') set unitMeasurement(value: string) {
    this._unitMeasurement = value;
    this.addGlucosaValidator();
    this.glycemicProfile.controls.glucose.updateValueAndValidity({ emitEvent: false });
  }

  ngOnInit() {
    // Diabetes obligatoria
    this.glycemicProfile.controls.diabetes.addValidators(Validators.required);

    // Si existe formulario lo seteamos
    if (this.formGroupDirective.form) {
      this.formGroupDirective.form.setControl('glycemicProfile', this.glycemicProfile);
    }

    this.metabolicoService.glycemicComplicationsGet().subscribe({
      next: (data) => {
        const glycemicComplications = data as unknown as GlycemicComplicationNode[];

        this.glycemicComplications = glycemicComplications;
        this.glycemicComplicationsKeys = this.extractKeys(glycemicComplications);
        this.neurologicalComplicationsKeys = this.extractKeys(glycemicComplications, 'NEUROLOGICAL');
        this.retinopathyComplicationsKeys = this.extractKeys(glycemicComplications, 'RETINOPATHY');
      }
    });

    // Validadores de cantidades en glucosa y HBA1C
    this.addGlucosaValidator();
    this.addHbA1cValidator();
    this.addYearsSinceDiabetesDiagnosisValidator();

    // Escuchar cambios en el campo diabetes
    this.glycemicProfile.controls.diabetes.valueChanges.subscribe((value) => {
      this.enableDisable();
    });

    // Escuchar cambios en hasTreatment
    this.glycemicProfile.controls.hasTreatment.valueChanges.subscribe((value) => {
      if (value !== 'si') {
        this.glycemicProfile.controls.treatment.setValue([]);
      }
    });

    // Escuchar cambios en complicationsExists
    this.glycemicProfile.controls.complicationsExists.valueChanges.subscribe((hasComplications) => {
      this.updateYearsSinceDiagnosisValidation(hasComplications);
      if (!hasComplications) {
        this.glycemicProfile.controls.complications.setValue([]);
        this.glycemicProfile.controls.neurologicalType.setValue('');
        this.glycemicProfile.controls.retinopatiaType.setValue('');
      }
    });

    // Escuchar cambios en complications para limpiar subtipos al deseleccionar
    this.glycemicProfile.controls.complications.valueChanges.subscribe((values: string[]) => {
      if (!values?.includes('NEUROLOGICAL')) {
        this.glycemicProfile.controls.neurologicalType.setValue('', { emitEvent: false });
      }
      if (!values?.includes('RETINOPATHY')) {
        this.glycemicProfile.controls.retinopatiaType.setValue('', { emitEvent: false });
      }
    });

    // Llamar enableDisable inicialmente para establecer el estado correcto
    this.enableDisable();
  }

  extractKeys(nodes: GlycemicComplicationNode[], parentKey?: string): string[] {
    const keys: string[] = [];
    for (const node of nodes) {
      if (node.key) {
        if (!parentKey) {
          keys.push(node.key);
        } else if (node.key === parentKey) {
          for (const child of node.childrens ?? []) {
            if (child.key) {
              keys.push(child.key);
            }
          }
        }
      }
    }
    return keys;
  }

  getKeyTranslate(key: string) {
    return `METABOLIC-CALC.COMPLICATIONS-OPTIONS.${key}`;
  }


  // Método para actualizar la validación de años desde diagnóstico
  updateYearsSinceDiagnosisValidation(hasComplications: boolean) {
    const yearsSinceControl = this.glycemicProfile.controls.yearsSinceDiabetesDiagnosis;

    // Guardar el validador de rango actual
    const rangeValidator = CalcValidators.yearsSinceDiabetesDiagnosis(0, this._age);

    // Limpiar validadores actuales
    yearsSinceControl.clearValidators();

    yearsSinceControl.addValidators(rangeValidator);

    yearsSinceControl.updateValueAndValidity({ emitEvent: false });
  }

  // Funcion para gestionar los campos del formulario
  enableDisable() {
    if (this.glycemicProfile.controls.diabetes.value === true) {
      this.glycemicProfile.controls.yearsSinceDiabetesDiagnosis.enable();

      const hasComplications = this.glycemicProfile.controls.complicationsExists.value;
      if (hasComplications) {
        this.updateYearsSinceDiagnosisValidation(true);
      }
    } else {
      this.glycemicProfile.controls.yearsSinceDiabetesDiagnosis.disable();
      this.glycemicProfile.controls.yearsSinceDiabetesDiagnosis.setValue('');
    }
  }

  // eslint-disable-next-line @angular-eslint/use-lifecycle-interface
  ngAfterViewInit(): void {
    if (this.data) {
      this.glycemicProfile.patchValue(this.data);
      // Verificar estado inicial después de cargar datos
      if (this.glycemicProfile.controls.diabetes.value === true && this.glycemicProfile.controls.complicationsExists.value === true) {
        this.updateYearsSinceDiagnosisValidation(true);
      }
    }
  }

  isRequired(control: AbstractControl) {
    try {
      return control.hasValidator(Validators.required);
    } catch (error) {
      return false;
    }
  }

  addGlucosaValidator() {
    const control = this.glycemicProfile.controls.glucose;
    const isRequired = this.isRequired(control);
    control.clearValidators();
    if (isRequired) {
      control.addValidators(Validators.required);
    }
    if (this._unitMeasurement === 'mmol/l') {
      control.addValidators(CalcValidators.glucose(1.66, 16.65, this._unitMeasurement));
    } else if (this._unitMeasurement === 'mg/dl') {
      control.addValidators(CalcValidators.glucose(30, 450, this._unitMeasurement));
    }
  }

  addHbA1cValidator() {
    this.glycemicProfile.controls.HBA1C.addValidators([CalcValidators.hbA1c(0, 20, '%')]);
  }

  onTreatmentChange(value: string, checked: boolean) {
    const treatment: string[] = this.glycemicProfile.controls.treatment.value || [];
    if (checked) {
      this.glycemicProfile.controls.treatment.setValue([...treatment, value]);
    } else {
      this.glycemicProfile.controls.treatment.setValue(treatment.filter((v) => v !== value));
    }
  }

  onComplicationChange(value: string, checked: boolean) {
    const complications: string[] = this.glycemicProfile.controls.complications.value || [];
    if (checked) {
      this.glycemicProfile.controls.complications.setValue([...complications, value]);
    } else {
      this.glycemicProfile.controls.complications.setValue(complications.filter((v) => v !== value));
      if (value === 'neurologicas') {
        this.glycemicProfile.controls.neurologicalType.setValue('');
      }
      if (value === 'retinopatia') {
        this.glycemicProfile.controls.retinopatiaType.setValue('');
      }
    }
  }

  addYearsSinceDiabetesDiagnosisValidator() {
    const control = this.glycemicProfile.controls.yearsSinceDiabetesDiagnosis;
    const isRequired = this.isRequired(control);
    if (typeof this._age === 'number') {
      control.clearValidators();
      control.addValidators(CalcValidators.yearsSinceDiabetesDiagnosis(0, this._age));
      if (isRequired) {
        control.addValidators(Validators.required);
      }
      control.updateValueAndValidity({ emitEvent: false });
    }
  }
}
